import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import MainPage from "@/pages/MainPage";
import DashboardPage from "@/pages/DashboardPage";
import NursesPage from "@/pages/NursesPage";
import RemindersPage from "@/pages/RemindersPage";
import RetinalAnalysisPage from "@/pages/RetinalAnalysisPage";
import Verify2FAPage from "@/pages/Verify2FAPage";
import SuppliesPage from "@/pages/SuppliesPage";
import ChatPage from "@/pages/ChatPage";
import DocumentsPage from "@/pages/DocumentsPage";
import NoticesPage from "@/pages/NoticesPage";
import GravesPage from "@/pages/GravesPage";
import ConsentsPage from "@/pages/ConsentsPage";
import UlcersPage from "@/pages/UlcersPage";
import AdminPage from "@/pages/AdminPage";
import CalculatorsPage from "@/pages/CalculatorsPage";
import PatientDataPage from "@/pages/PatientDataPage";
import MediVoicePage from "@/pages/MediVoicePage";
import MediVoiceSessionPage from "@/pages/MediVoiceSessionPage";
import ThyroidClinicPage from "@/pages/ThyroidClinicPage";

let BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Strip www. to avoid cross-origin redirect issues (www → non-www 308 lacks CORS headers)
if (BACKEND_URL?.includes("://www.")) {
  BACKEND_URL = BACKEND_URL.replace("://www.", "://");
}
export const API = `${BACKEND_URL}/api`;

// Redirect www to non-www (required for Google Auth and CORS consistency)
if (typeof window !== "undefined" && window.location.hostname.startsWith("www.")) {
  window.location.href = window.location.href.replace("://www.", "://");
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  const checkAuth = async () => {
    // Don't check auth if we're processing a callback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    
    try {
      // Try to get session token from localStorage as backup
      const storedToken = localStorage.getItem("session_token");
      const headers = storedToken ? { "Authorization": `Bearer ${storedToken}` } : {};
      
      const response = await fetch(`${API}/auth/me`, {
        headers,
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData.user || userData);
      } else {
        setUser(null);
        localStorage.removeItem("session_token");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const storedToken = localStorage.getItem("session_token");
      const headers = storedToken ? { "Authorization": `Bearer ${storedToken}` } : {};
      
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers,
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
    localStorage.removeItem("session_token");
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, setLoading, logout, checkAuth, isProcessingCallback, setIsProcessingCallback }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = ({ onRequire2FA }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setLoading } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        setLoading(false);
        navigate("/login", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      // Prevent double processing even across remounts/reloads (PWA, service worker)
      const processedKey = `auth_processed_${sessionId}`;
      if (sessionStorage.getItem(processedKey)) {
        window.history.replaceState(null, "", window.location.pathname);
        setLoading(false);
        return;
      }
      sessionStorage.setItem(processedKey, "1");

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          
          // Check if 2FA is required — lift state up before clearing hash
          if (data.requires_2fa) {
            onRequire2FA({ email: data.email });
            window.history.replaceState(null, "", window.location.pathname);
            setLoading(false);
            return;
          }
          
          // Direct login (should not happen anymore — 2FA always required)
          if (data.session_token) {
            localStorage.setItem("session_token", data.session_token);
          }
          setUser(data.user);
          setLoading(false);
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/", { replace: true });
        } else {
          const errData = await response.json().catch(() => ({}));
          console.error("Auth session error:", response.status, errData);
          setLoading(false);
          navigate("/login", { replace: true, state: { error: errData.detail || "Sisselogimine ebaõnnestus" } });
        }
      } catch (error) {
        console.error("Auth callback failed:", error);
        setLoading(false);
        navigate("/login", { replace: true, state: { error: "Võrguühenduse viga" } });
      }
    };

    processAuth();
  }, [location, navigate, setUser, setLoading, onRequire2FA]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 spinner" />
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If there's a session_id in hash, don't redirect - let AuthCallback handle it
  if (location.hash?.includes("session_id=")) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 spinner" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [twoFAState, setTwoFAState] = useState(null);

  // Show 2FA verification page (state persists even after hash is cleared)
  if (twoFAState && !user) {
    return (
      <Verify2FAPage
        email={twoFAState.email}
        onBack={() => {
          setTwoFAState(null);
          navigate("/login", { replace: true });
        }}
        onSuccess={() => {
          setTwoFAState(null);
        }}
      />
    );
  }

  // Check for session_id in URL hash - must be synchronous
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback onRequire2FA={setTwoFAState} />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={<Navigate to="/schedule" replace />}
      />
      <Route
        path="/nurses"
        element={
          <ProtectedRoute>
            <NursesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders"
        element={
          <ProtectedRoute>
            <RemindersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/retinal"
        element={
          <ProtectedRoute>
            <RetinalAnalysisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies"
        element={
          <ProtectedRoute>
            <SuppliesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notices"
        element={
          <ProtectedRoute>
            <NoticesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/graves"
        element={
          <ProtectedRoute>
            <GravesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consents"
        element={
          <ProtectedRoute>
            <ConsentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ulcers"
        element={
          <ProtectedRoute>
            <UlcersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/patient-data"
        element={
          <ProtectedRoute>
            <PatientDataPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calculators"
        element={
          <ProtectedRoute>
            <CalculatorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/medivoice"
        element={
          <ProtectedRoute>
            <MediVoicePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/medivoice/session/:sessionId"
        element={
          <ProtectedRoute>
            <MediVoiceSessionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/thyroid"
        element={
          <ProtectedRoute>
            <ThyroidClinicPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
